import React from 'react';
import { Box } from '@mui/material';

/**
 * Tabs that own the full main viewport (edge-to-edge, no outer Paper box).
 * Each module manages its own internal toolbar + scroll regions.
 */
export const FULL_VIEWPORT_TABS = new Set([
  'ChatOperations',
  'Tickets',
  'Kanban',
  'SprintPlanning',
  'Workspace',
  'CrmMarketing',
  'Reports',
  'GameStudio',
  'ImageEditor',
]);

/** @param {string} tabId */
export function isFullViewportTab(tabId) {
  return FULL_VIEWPORT_TABS.has(tabId);
}

/**
 * Tab panel sx — full-viewport modules fill height; others scroll inside padded area.
 * @param {string} tabId
 * @param {string} activeTab
 */
export function dashboardTabPanelSx(tabId, activeTab) {
  const isActive = activeTab === tabId;
  const fullViewport = isFullViewportTab(tabId);

  return {
    display: isActive ? 'flex' : 'none',
    flexDirection: 'column',
    flex: isActive ? 1 : undefined,
    minHeight: isActive ? 0 : undefined,
    height: isActive ? '100%' : undefined,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: isActive ? (fullViewport ? 'hidden' : 'auto') : 'hidden',
    px: fullViewport ? 0 : { xs: 1.5, sm: 2 },
    py: fullViewport ? 0 : { xs: 1.5, sm: 2 },
  };
}

/**
 * Main content `<main>` shell — fixed sidebar offset, no boxed padding.
 */
export function dashboardMainSx({ drawerWidth, hasCompanyLinkBar }) {
  return {
    flexGrow: 1,
    p: 0,
    width: { sm: `calc(100% - ${drawerWidth}px)` },
    minWidth: 0,
    minHeight: 0,
    mt: hasCompanyLinkBar ? { xs: '100px', sm: '108px' } : 8,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };
}

/**
 * Standard edge-to-edge module root (Tickets Hub, Kanban, Settings, etc.).
 */
export function DashboardModuleRoot({ children, sx, ...props }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        width: '100%',
        bgcolor: 'background.paper',
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

/** Compact horizontal toolbar row used across ops modules. */
export function DashboardModuleToolbar({ children, sx, ...props }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        px: 1.5,
        py: 0.75,
        minHeight: 44,
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

/** Scrollable body region below a module toolbar. */
export function DashboardModuleBody({ children, sx, ...props }) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        width: '100%',
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

/**
 * Wraps tab panel content in the dashboard main area.
 */
export function DashboardMainContent({ drawerWidth, hasCompanyLinkBar, children }) {
  return (
    <Box component="main" sx={dashboardMainSx({ drawerWidth, hasCompanyLinkBar })}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
